import { ISupplier } from '@bridebook/models/source/models/Suppliers.types';
import { IAchievement } from '@bridebook/models/source/models/Suppliers/Achievements.types';
import { ISupplierAdmin } from '@bridebook/models/source/models/Suppliers/Admin/admin.types';
import { ISupplierAdminScore } from '@bridebook/models/source/models/Suppliers/Admin/score.types';
import { ISupplierAdminStats } from '@bridebook/models/source/models/Suppliers/Admin/stats.types';
import { IBadge } from '@bridebook/models/source/models/Suppliers/Badges.types';
import { IFair } from '@bridebook/models/source/models/Suppliers/Fairs.types';
import { IFeedback } from '@bridebook/models/source/models/Suppliers/Feedback.types';
import { IOffer } from '@bridebook/models/source/models/Suppliers/Offers.types';
import { IPackage } from '@bridebook/models/source/models/Suppliers/Packages.types';
import { IPhoto } from '@bridebook/models/source/models/Suppliers/Photos.types';
import { IQuestion } from '@bridebook/models/source/models/Suppliers/Questions.types';
import { IRecommendation } from '@bridebook/models/source/models/Suppliers/Recommendations.types';
import { IVideo } from '@bridebook/models/source/models/Suppliers/Videos.types';
import { IWork } from '@bridebook/models/source/models/Suppliers/Works.types';
import { ISupplierCalendar } from '@bridebook/models/source/models/Suppliers/Calendars.types';
import { ISupplierCalendarSchedule } from '@bridebook/models/source/models/Suppliers/Calendars/Schedules.types';
import { ISupplierCalendarEvent } from '@bridebook/models/source/models/Suppliers/Calendars/Events.types';
import { PremiumTiers as VenueTier } from '@bridebook/toolbox/src/constants';
import { NamedQuery } from '@bridebook/toolbox/src/search-suppliers/types';
import { OmitTimestamps } from '@bridebook/toolbox/src/elastic-sync/omit-timestamps';
import { Coordinates } from '@bridebook/toolbox/src/elastic-sync/utils/counties-coordinates';
import { MappedFair } from '@bridebook/toolbox/src/elastic-sync/utils/processVenueFairs';
import { SupplierRelation } from '@bridebook/toolbox/src/elastic-sync/suppliers/get-relations';
import { ScoreCoefficients } from '@bridebook/toolbox/src/elastic-sync/suppliers/scores/coefficients';

export interface IFeedbackBody {
  activePage: number;
  feedback: Partial<IFeedback>[];
  hasReviews: boolean;
  hasTestimonials: boolean;
  stars: number;
}

export interface IShowroundViewingDates {
  schedules: ISupplierCalendarSchedule[];
  events: ISupplierCalendarEvent[];
  externalConflictEvents: ISupplierCalendarEvent[];
  calendar: ISupplierCalendar | null;
}

export interface ISupplierDataAll {
  admin: ISupplierAdmin;
  achievements?: OmitTimestamps<IAchievement>[];
  badges?: OmitTimestamps<IBadge>[];
  score: OmitTimestamps<ISupplierAdminScore>;
  stats?: OmitTimestamps<ISupplierAdminStats>;
  photos: OmitTimestamps<IPhoto>[];
  offers: OmitTimestamps<IOffer>[];
  packages: OmitTimestamps<IPackage>[];
  recommendations?: OmitTimestamps<IRecommendation>[];
  recommendedBy?: string[];
  videos: OmitTimestamps<IVideo>[];
  supplier: ISupplier;
  fairs: OmitTimestamps<IFair>[];
  feedback: IFeedbackBody;
  questions?: OmitTimestamps<IQuestion>[];
  relations?: SupplierRelation[];
  relatedBy?: string[];
  works?: { active: IWork[]; all: IWork[] };
  showroundViewingDates: IShowroundViewingDates;
  openDayEvents: ISupplierCalendarEvent[];
}

export interface IVendorCommonProps {
  additionalAreas?: string[];
  additionalAreasLocation?: LocationType;
  additionalCountries?: (string | '*')[];
  adminAreaLevel1: string;
  adminAreaLevel2: string;
  appearsInMainSearch: boolean;
  bestOfBritain: boolean;
  conciergeOptOut: boolean;
  coefficients: ScoreCoefficients;
  connections?: {
    recommendedBy?: string[];
    worksWith: string[];
    recommends: string[];
    relatedBy?: string[];
    relatesWith: string[];
  };
  country: string; // TODO: deprecate in favour of adminAreaLevel1
  countryCode: string;
  county: string; // TODO: deprecate in favour of adminAreaLevel2
  currency: string;
  customArea?: string;
  deleted: boolean;
  delisted: boolean;
  description: string;
  dropFactor: number;
  email: string;
  exposureScore?: number;
  conversionScore?: number;
  fairs?: MappedFair[];
  groupParent?: string;
  hasBadDebtCoupleside?: boolean;
  hasBridebookAwards: boolean;
  hasBridebookOffer: boolean;
  hasIndustryAwards: boolean;
  id: string;
  inclusivityAsianOwned: boolean;
  inclusivityAwardWinner: boolean;
  inclusivityBlackOwned: boolean;
  inclusivityLgbtqPlusExpert: boolean;
  inclusivityMultiFaith: boolean;
  inclusivityNeurodiverseFriendly: boolean;
  inclusivitySustainabilityHero: boolean;
  inclusivityWomanOwned: boolean;
  matchedBy?: NamedQuery[] | string[];
  lateAvailabilityDates?: string[];
  location: LocationType;
  locationShape: { type: string; coordinates: [number, number] };
  name: string;
  phone?: string;
  phone9: string;
  photos: string;
  photosCount?: ISupplierDataAll['supplier']['counters']['photos'];
  popularityScore: number;
  postalCode: string;
  postalCodeRaw: string;
  pricingAffordable: boolean;
  pricingIndicator?: string;
  pricingLuxury: boolean;
  pricingModerate: boolean;
  pricingSuperLuxury: boolean;
  profileScore: number;
  publicId: string;
  registrationDate: number;
  responseScore: number;
  reviewsCount?: number;
  reviewsOnlyCount?: number;
  reviewsStars?: number;
  score: number;
  searchable: boolean;
  searchBoost?: number;
  searchBoostActiveEnd?: string;
  searchBoostActiveStart?: string;
  searchBoostForType?: string;
  seoIndex: boolean;
  seoScore: number;
  seoUrlSlug?: string;
  statusMessage: string;
  street: string;
  supplierBoosted: boolean;
  testimonialsOnlyCount?: number;
  thumbnail: string;
  tier: number;
  timezone?: ISupplier['l10n']['timezone'];
  town: string;
  translations: ISupplier['_translations'];
  translationLocales: string[];
  trustedPartnerSince?: number;
  type: string;
  videosCount?: ISupplierDataAll['supplier']['counters']['videos'];
  website?: string;
  weddingsHosted?: [number] | [number, number];
  contacts?: {
    calendarUrl?: string;
  };
}

export type withIndexExtra<T> = T & {
  database?: string;
  indexedAt: string;
};

export type Scores = {
  award: number;
  endorsement: number;
  profile: number;
  contact: number;
  photo: number;
  recommendation: number;
  popularity?: number;
  response?: number;
  seo: number;
};

export type LocationType = Coordinates[] | Coordinates;

export { VenueTier };

export enum SupplierTier {
  /**
   * Limited Profile
   */
  Unverified = -1,
  /**
   * Novice
   */
  Tier_0 = 0,
  /**
   * Basic
   */
  Tier_1 = 1,
  /**
   * Classic
   */
  Tier_2 = 2,
}

export const isPaidTier = (tier?: VenueTier) =>
  tier &&
  [
    VenueTier.Tier_0_25,
    VenueTier.Tier_0_5,
    VenueTier.Tier_1,
    VenueTier.Tier_2,
    VenueTier.Tier_2_5,
    VenueTier.Tier_3,
    VenueTier.Tier_4,
  ].includes(tier);

export const isPaidSupplierTier = (tier?: SupplierTier) =>
  tier && [SupplierTier.Tier_1, SupplierTier.Tier_2].includes(tier);

export const isVenue = (supplier: ISupplierDataAll['supplier']) => supplier.type?.[0] === 'venue';
